import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import classes from "./CardDraggable.module.scss"
import clsx from 'clsx'
import {
  motion,
  useAnimation,
  useTransform,
  useMotionValue,
  easeOut
} from "framer-motion";
import { Card } from "../Card";
import { YesNoButton } from "../Card/YesNoButton";
import { useGameSounds } from "@educaplay/core";
import { NAME_SOUND_ACTIVE } from "@educaplay/core/utils";
import { RESPONSES } from "../../utils/constants";
import { useSelector } from "react-redux";
import { SCREENS } from "@educaplay/store/constants";

const MAX_DISTANCE = 150;
const SELECT_DISTANCE = 30;

const iOS = /iPad|iPhone|iPod/.test(navigator.platform)

const CardDraggable = forwardRef(function CardDraggable(props, ref) {
  const { onAnswer, text, imgSrc, audioSrc, isNextCard = false } = props;

  const gameSounds = useGameSounds();

  const lives = useSelector(state => state.game.lives);
  const screen = useSelector(state => state.game.view);
  const [hasResponded, setHasResponded] = useState(false);
  const [currentResponse, setCurrentResponse] = useState(null);
  const animationControls = useAnimation();
  const [constraints, setConstraints] = useState(() => ({
    minDistance: Math.min(window.innerWidth * 0.15, MAX_DISTANCE),
    fullDistance: window.innerWidth * 0.8
  }));
  const currentQuestion = useSelector(state => state.game.questions[state.game.currentIndex]);
  const liveActived = useSelector(state => state.game.liveActived);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-12, 12]);

  useImperativeHandle(ref, () => ({
    fail: async () => {
      setHasResponded(true)
      await animationControls.start({ x: 0, y: window.innerHeight }, { duration: 0.4, ease: easeOut });
    }
  }));

  useEffect(() => {
    const handleResize = () => {
      setConstraints({
        minDistance: Math.min(window.innerWidth * 0.15, MAX_DISTANCE),
        fullDistance: window.innerWidth * 0.8
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleChange = (value) => {
      if (Math.abs(value) > SELECT_DISTANCE) {
        const response = value < 0 ? RESPONSES.NO : RESPONSES.YES;

        setCurrentResponse(currentResponse => {
          if (currentResponse !== response) {
            gameSounds.play(NAME_SOUND_ACTIVE)
          }

          return response
        })
      } else {
        setCurrentResponse(null);
      }
    };

    const unsubscribe = x.on("change", handleChange);

    return () => {
      unsubscribe();
    };
  }, [x, constraints.minDistance, gameSounds]);

  useEffect(() => {

    const handleKeyDown = (event) => {
      if (isNextCard || hasResponded || !lives) return;

      if (event.key === "ArrowRight") {
        handleClickYes()
      } else if (event.key === "ArrowLeft") {
        handleClickNo()
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNextCard, hasResponded])

  const handleDragEnd = async (_event, info) => {
    if (hasResponded) return;

    if (Math.abs(info.offset.x) < SELECT_DISTANCE) {
      gameSounds.play('yesorno-put');
      animationControls.start({ x: 0, y: 0 }, { duration: 0.2 });
      return;
    }

    gameSounds.play('yesorno-drag');
    setHasResponded(true);
    const y = info.offset.y + constraints.fullDistance * 0.1;

    if (info.offset.x < 0) {
      onAnswer(RESPONSES.NO, { showFeedback: !(currentQuestion.checkAnswer(RESPONSES.NO) && !liveActived) });
      await animationControls.start({
        y,
        x: -constraints.fullDistance * 1.4,
        transition: { duration: 0.4, ease: "easeIn" }
      });

    } else {
      onAnswer(RESPONSES.YES, { showFeedback: !(currentQuestion.checkAnswer(RESPONSES.YES) && !liveActived) });
      await animationControls.start({
        y,
        x: constraints.fullDistance * 1.4,
        transition: { duration: 0.4, ease: "easeIn" }
      });
    }
  };

  const handleClickYes = async () => {
    setHasResponded(true);
    gameSounds.play('yesorno-drag');

    onAnswer(RESPONSES.YES, { showFeedback: !(currentQuestion.checkAnswer(RESPONSES.YES) && !liveActived) });

    await animationControls.start({
      x: constraints.fullDistance * 1.5,
      y: constraints.fullDistance * 0.08,
      transition: { duration: 0.6, ease: "easeIn" }
    });

  };

  const handleClickNo = async () => {
    setHasResponded(true);
    gameSounds.play('yesorno-drag');
    onAnswer(RESPONSES.NO, { showFeedback: !(currentQuestion.checkAnswer(RESPONSES.NO) && !liveActived) });

    await animationControls.start({
      x: -constraints.fullDistance * 1.5,
      y: constraints.fullDistance * 0.08,
      transition: { duration: 0.6, ease: "easeIn" }
    });
  };

  return (
    <motion.div className={clsx(classes.container, { [classes.noInteractiveCard]: isNextCard, [classes.disableSelection]: iOS })}>
      <motion.div
        drag
        whileTap={{
          scale: 1.025,
          boxShadow: "0px 1px 0px 0px #11200059, 0px 16px 0px 0px #00000026",
          cursor: "grabbing"
        }}
        onPointerDown={() => {
          gameSounds.play('yesorno-move');
        }}
        animate={animationControls}
        style={{
          x,
          y,
          rotate: rotate
        }}
        dragElastic={0.2}
        dragConstraints={{
          left: -constraints.minDistance,
          right: constraints.minDistance
        }}
        onDragEnd={handleDragEnd}
        className={classes.card}
      >

        <Card
          activeYes={currentResponse === RESPONSES.YES}
          activeNo={currentResponse === RESPONSES.NO}
          text={text}
          imgSrc={imgSrc}
          audioSrc={audioSrc}
          freezed={isNextCard}
        />

      </motion.div>

      {(!isNextCard && screen !== SCREENS.SCORE_SCREEN) && (
        <div className={classes["button-yesno-container"]}>

          <YesNoButton disabled={hasResponded} variant="no" active={currentResponse === RESPONSES.NO} onClick={handleClickNo} />
          <YesNoButton disabled={hasResponded} variant="yes" active={currentResponse === RESPONSES.YES} onClick={handleClickYes} />
        </div>
      )}

    </motion.div>

  );
})

export default CardDraggable;


